import {Add, Edit, LinkOffOutlined, PhotoCameraOutlined, SettingsOutlined} from '@mui/icons-material';
import {useCan, useCanAny, usePersona} from 'src/permissions/hooks';
import {Box, Button, Typography} from '@mui/material';
import {FC, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {chargersApi} from 'src/api';
import {useBusinessId, useAppDispatch, useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {DropZoneModal} from 'src/components/common/DropZoneModal';
import {UserCell} from 'src/components/users/UserCell';
import {ColoredStatus} from 'src/components/common/ColoredStatus';
import {actionButtonSx} from 'src/components/common/actionButton.sx';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {RecordActionBar} from 'src/components/mobile/RecordActionBar';
import {actionSlotSx, squareActionSx} from 'src/components/mobile/RecordActionBar/RecordActionBar.sx';
import {RecordIdentityCard} from 'src/components/mobile/RecordIdentityCard';
import {
    cardSx,
    deviceBadgeSx,
    deviceBodySx,
    deviceHeaderSx,
    deviceLockSx,
    deviceTitleSx,
    factFooterButtonSx,
    factEmptySx,
    factLabelSx,
    factSx,
    factValueSx,
    factsSx,
    headerSx,
    photoSx,
    subtitleSx,
    titleSx,
} from './ChargerIdentity.sx';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch, UserBexioDataDTO} from 'src/types';
import {blankPhoto} from 'src/utils/consts';
import {ChargerDriverActions} from '../ChargerDriverActions';
import {ChargerResetWrapper} from '../ChargerResetWrapper';
import {ChargerUpdateFormWrapper} from '../ChargerUpdateFormWrapper';
import {
    buttonImageWrapperSx,
    costBoxSx,
    deleteButtonSx,
    detailItemSx,
    detailsInnerSx,
    detailsTitleSx,
    detailsWrapperSx,
    editButtonSx,
    editCostBtnSx,
    editCostIconSx,
    imageButtonSx,
    imageInnerSx,
    imageWrapperSx,
    modalBodySx,
    statusPillSx,
    userBoxSx,
} from './ChargerDetails.sx';
import {ChargerDetailsType} from './ChargerDetails.types';
import theme from '../../../theme';
import {generateItemCost, isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {EditChargerCostForm} from '../../forms/EditChargerCostForm';
import {CurrencyEnum} from '../../../enums';
import dayjs from 'dayjs';
import {EditChargerCostFormType} from '../../forms/formData/editChargerCost';

type AddImageMutationTrigger = ReturnType<typeof chargersApi.useAddChargerImageMutation>[0];

type DeleteUserMutationTrigger = ReturnType<typeof chargersApi.useDeleteUserMutation>[0];

const handleTrigger = async (
    file: File[],
    chargerId: number,
    trigger: AddImageMutationTrigger,
    dispatch: AppDispatch,
    handleCloseImageModal: () => void,
    handleBlockSubmit: (status: boolean) => void,
    businessId?: number
) => {
    try {
        handleBlockSubmit(true);
        dispatch(preloaderChangeStatus(true));
        const formData = new FormData();
        formData.append('file', file[0]);
        formData.set('businessId', String(businessId));
        await trigger({data: formData, chargerId}).unwrap();
        handleCloseImageModal();
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        handleBlockSubmit(false);
        dispatch(preloaderChangeStatus(false));
    }
};

const handleDeleteUser =
    (
        deleteUser: DeleteUserMutationTrigger,
        userId: number,
        businessId: number,
        chargerId: number,
        closeModal: () => void
    ) =>
        async () => {
            try {
                await deleteUser({
                    userId,
                    businessId,
                    chargerId,
                });
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                closeModal();
            }
        };

export const ChargerDetails: FC<ChargerDetailsType> = ({data}) => {
    // Editing a cost item and adding one are separate endpoints with separate permissions;
    // the personas listed here held neither, and so do several the list never mentioned.
    const canUpdateChargerCost = useCan('chargerCosts.update');
    const canCreateChargerCost = useCan('chargerCosts.create');
    const intl = useIntl();
    const persona = usePersona();
    const dispatch = useAppDispatch();

    const [open, setOpen] = useState<boolean>(false);
    const [openDropZone, setOpenDropZone] = useState<boolean>(false);
    const [chargerImage, setChargerImage] = useState<File[]>();
    const [showEditCost, setShowEditCost] = useState(false);
    const [blockSubmit, setBlockSubmit] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const cost = data?.itemCosts?.[0];

    const costInitValue: EditChargerCostFormType = {
        name: cost ? cost?.name : `Charge Point: ${data.chargePointId}`,
        cost: cost ? cost?.initCost : 0,
        amortizedCost: cost ? cost?.currentCost : 0,
        currency: cost ? cost?.currency : CurrencyEnum.CHF,
        date: cost ? cost?.installationDate : dayjs.utc().valueOf(),
        serviceLife: cost ? cost?.serviceLife : 0,
    };

    const dialogTitle = intl.formatMessage({id: 'editCharger'});
    const uploadDialogTitle = intl.formatMessage({id: 'addNewImage'});
    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    // The cable lock is an OCPP ChangeConfiguration on the device, so it is offered to whoever may
    // send one. Named as "a driver or a company", it was hidden from the business that owns the
    // charger and from the platform administrator, both of whom hold the permission.
    // The cable lock has its own permission: it writes one key and reads one key, and requiring
    // `chargers.configure` — the right to reprogram the device — kept it from the people whose cable
    // it is, while handing everyone who needed it the run of the charger's settings.
    const canToggleCableLock = useCan('chargers.cableLock');
    const canUpdateCharger = useCan('chargers.update');
    // What the charger cost and is still worth. It used to ask for costItems.export - exporting the
    // cost lines of charging sessions, which every customer holds - so drivers, employees and
    // companies saw the asset value of the charger they charge on.
    const canReadCostData = useCan('chargerCosts.read');
    // The device card's action row shows whichever of its four actions the caller may perform; it is
    // there as long as that is at least one of them. It used to hang on chargers.reset alone, which
    // hid the two read-only destinations behind the right to reboot a charger.
    const canUseDeviceActions = useCanAny(
        'chargers.reset', 'chargers.readQrCode', 'chargers.readConfig', 'chargerLogs.search');
    // Detaching a client used to be guarded by `chargerCosts.create` - a mechanical replacement of an
    // older `!isRealEstate` check that reached for whatever permission was already in scope. Pricing
    // has nothing to do with it, and the pairing hid the button from anyone allowed to assign users
    // but not to price them.
    const canAssignChargerUser = useCan('chargers.assignUser');
    const isEndUserRole =
        persona.isDriver ||
        persona.isEmployee ||
        persona.isCompany;
    const [trigger] = chargersApi.useAddChargerImageMutation();
    const [deleteUser] = chargersApi.useDeleteUserMutation();

    const handleShowDeleteUserModal = (status: boolean) => () => {
        setShowDeleteModal(status);
    };

    const handleShowEditCostModal = (status: boolean) => () => {
        setShowEditCost(status);
    };

    const handleBlockSubmit = (status: boolean) => {
        setBlockSubmit(status);
    };

    const handleOpenModal = () => {
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    const handleOpenImageModal = () => {
        setOpenDropZone(true);
    };

    const handleCloseImageModal = () => {
        setOpenDropZone(false);
    };

    const handleSetImage = (file: File[] | null) => {
        file && setChargerImage(file);
    };

    const handleSubmit = () => {
        chargerImage &&
        handleTrigger(
            chargerImage,
            data.id,
            trigger,
            dispatch,
            handleCloseImageModal,
            handleBlockSubmit,
            data.business.id
        );
    };

    const costTotals = generateItemCost(data.itemCosts ?? []);
    const {isCompact} = useScreenTier();

    /*
     * A company carries its Bexio data on the nested owner (company.user), not at the top level -
     * lift it so UserCell reads it like a driver's and doesn't falsely flag the name red.
     */
    const clientCellData = data.user || {
        ...data.company,
        business: data.business,
        bexioDataList: (data.company as unknown as {user?: {bexioDataList?: UserBexioDataDTO[]}})?.user?.bexioDataList,
    };

    /** A cell's own action on a phone: a line under the value, not a table cell's footer. */
    const phoneCellActionSx = {
        mt: 1,
        mx: 0,
        width: 'auto',
        height: '36px',
        borderRadius: '8px',
        justifyContent: 'flex-start',
        borderTop: 'none',
        background: 'transparent',
    };

    /** The facts the wide card states, with the actions that belong to them kept attached. */
    const phoneFacts = [
        {
            id: 'client',
            label: <FormattedMessage id="client"/>,
            value: (
                <>
                    {data.user || data.company
                        ? <UserCell data={clientCellData}/>
                        : <FormattedMessage id="notSet"/>}
                    {canAssignChargerUser && (data.user || data.company) && (
                        <Button
                            sx={{...(factFooterButtonSx('danger') as object), ...phoneCellActionSx}}
                            startIcon={<LinkOffOutlined/>}
                            onClick={handleShowDeleteUserModal(true)}
                        >
                            <FormattedMessage id="unlink"/>
                        </Button>
                    )}
                </>
            ),
        },
        {
            id: 'location',
            label: <FormattedMessage id="location"/>,
            value: data.location?.name || <FormattedMessage id="noLocation"/>,
        },
        // A customer's charger is always their own business's, so for them the fact only repeats
        // who they signed with.
        ...(!isEndUserRole
            ? [{
                id: 'business',
                label: <FormattedMessage id="business"/>,
                value: data.business?.name ?? '-',
            }]
            : []),
        // Behind chargerCosts.read: the figure and its button appear together or not at all.
        ...(canReadCostData
            ? [{
                id: 'currentCostCharger',
                label: <FormattedMessage id="currentCostCharger"/>,
                value: (
                    <>
                        <Typography component="span" variant="caption" sx={{fontWeight: 400}}>Initial </Typography>
                        {costTotals.initTotal} {costTotals.currency}
                        <Typography component="span" variant="caption" sx={{fontWeight: 400}}> / Amortized </Typography>
                        {costTotals.amortizedTotal} {costTotals.currency}
                        {canUpdateChargerCost && !!data.itemCosts?.length && (
                            <Button
                                sx={{...(factFooterButtonSx() as object), ...phoneCellActionSx}}
                                startIcon={<Edit/>}
                                onClick={handleShowEditCostModal(true)}
                            >
                                <FormattedMessage id="edit"/>
                            </Button>
                        )}
                        {canCreateChargerCost && !data.itemCosts?.length && (
                            <Button
                                sx={{...(factFooterButtonSx() as object), ...phoneCellActionSx}}
                                startIcon={<Add/>}
                                onClick={handleShowEditCostModal(true)}
                            >
                                <FormattedMessage id="add"/>
                            </Button>
                        )}
                    </>
                ),
            }]
            : []),
    ];

    return (
        <>
            {isCompact && (
                <RecordIdentityCard
                    // The charger's own photograph rather than a glyph standing in for one.
                    icon={(
                        <img
                            src={data.image?.url ?? blankPhoto}
                            alt={data.name}
                        />
                    )}
                    title={data.name || data.chargePointId}
                    subtitle={(
                        <>
                            {[data.chargePointVendor, data.chargePointModel].filter(Boolean).join(' · ')}
                            {' · '}
                            <b>{data.chargePointId}</b>
                        </>
                    )}
                    status={data.currentStatus && <ColoredStatus label={data.currentStatus} fontSize={11}/>}
                    facts={phoneFacts}
                />
            )}

            {!isCompact && (
            <Box sx={cardSx}>
                <Box sx={headerSx}>
                    <Box sx={photoSx}>
                        <img
                            src={data.image?.url ?? blankPhoto}
                            alt={data.name}
                        />
                    </Box>
                    <Box sx={{minWidth: 0}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap'}}>
                            <Typography sx={titleSx}>{data.name || data.chargePointId}</Typography>
                            {data.currentStatus && <ColoredStatus label={data.currentStatus} fontSize={11}/>}
                        </Box>
                        {/* Vendor, model and the OCPP id, which the page heading used to run together
                            in one line with the id in brackets. */}
                        <Typography sx={subtitleSx}>
                            {[data.chargePointVendor, data.chargePointModel].filter(Boolean).join(' · ')}
                            {' · '}
                            <b>{data.chargePointId}</b>
                        </Typography>
                    </Box>

                    <Box sx={{flex: '1 1 auto'}}/>

                    {canUpdateCharger && (
                        <Button
                            variant="contained"
                            sx={actionButtonSx('32px', 'secondary')}
                            startIcon={<PhotoCameraOutlined/>}
                            onClick={() => setOpenDropZone(true)}
                        >
                            <FormattedMessage id="photo"/>
                        </Button>
                    )}
                    {canUpdateCharger && (
                        <Button
                            variant="contained"
                            sx={actionButtonSx('32px', 'secondary')}
                            startIcon={<Edit/>}
                            onClick={handleOpenModal}
                        >
                            <FormattedMessage id="edit"/>
                        </Button>
                    )}
                </Box>

                <Box sx={factsSx}>
                    <Box sx={factSx}>
                        <Typography sx={factLabelSx}>
                            <FormattedMessage id="client"/>
                        </Typography>
                        {data.user || data.company ? (
                            <>
                                <UserCell data={clientCellData}/>
                            </>
                        ) : (
                            <Typography sx={factEmptySx}>
                                <FormattedMessage id="notSet"/>
                            </Typography>
                        )}
                        {canAssignChargerUser && (data.user || data.company) && (
                            <Button
                                sx={factFooterButtonSx('danger')}
                                startIcon={<LinkOffOutlined/>}
                                onClick={handleShowDeleteUserModal(true)}
                            >
                                <FormattedMessage id="unlink"/>
                            </Button>
                        )}
                    </Box>

                    <Box sx={factSx}>
                        <Typography sx={factLabelSx}>
                            <FormattedMessage id="location"/>
                        </Typography>
                        <Typography sx={factValueSx}>
                            {data.location?.name || <FormattedMessage id="noLocation"/>}
                        </Typography>
                    </Box>

                    {/* A customer's charger is always their own business's, so for them the fact only
                        repeats who they signed with. */}
                    {!isEndUserRole && (
                        <Box sx={factSx}>
                            <Typography sx={factLabelSx}>
                                <FormattedMessage id="business"/>
                            </Typography>
                            <Typography sx={factValueSx}>{data.business?.name ?? '-'}</Typography>
                        </Box>
                    )}

                    {/* Behind chargerCosts.read: the figure and its two buttons appear together or
                        not at all. */}
                    {canReadCostData && (
                        <Box sx={factSx}>
                            <Typography sx={factLabelSx}>
                                <FormattedMessage id="currentCostCharger"/>
                            </Typography>
                            <Typography sx={factValueSx}>
                                <Typography component="span" variant="caption" sx={{fontWeight: 400}}>Initial </Typography>
                                {costTotals.initTotal} {costTotals.currency}
                                <Typography component="span" variant="caption" sx={{fontWeight: 400}}> / Amortized </Typography>
                                {costTotals.amortizedTotal} {costTotals.currency}
                            </Typography>
                            {canUpdateChargerCost && !!data.itemCosts?.length && (
                                <Button
                                    sx={factFooterButtonSx()}
                                    startIcon={<Edit/>}
                                    onClick={handleShowEditCostModal(true)}
                                >
                                    <FormattedMessage id="edit"/>
                                </Button>
                            )}
                            {canCreateChargerCost && !data.itemCosts?.length && (
                                <Button
                                    sx={factFooterButtonSx()}
                                    startIcon={<Add/>}
                                    onClick={handleShowEditCostModal(true)}
                                >
                                    <FormattedMessage id="add"/>
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
            )}

            {/* What you do TO the hardware, in its own card. It used to be the tail of the record of
                what the hardware IS, behind two dividers. Shown when the caller may do at least one of
                the things in it - the lock beside the title, or any of the four actions below. */}
            {(canUseDeviceActions || canToggleCableLock) && (
                <Box sx={cardSx}>
                    <Box sx={deviceHeaderSx}>
                        <Box sx={deviceBadgeSx}>
                            <SettingsOutlined/>
                        </Box>
                        <Typography sx={deviceTitleSx}>
                            <FormattedMessage id="device"/>
                        </Typography>
                        {/* The lock beside the title, not in the row of actions: it reports what the
                            device is doing, and the five below it are things you do to the device. */}
                        {canToggleCableLock && (
                            <Box sx={deviceLockSx}>
                                <ChargerDriverActions data={data}/>
                            </Box>
                        )}
                    </Box>
                    {canUseDeviceActions && (
                        <Box sx={deviceBodySx}>
                            <ChargerResetWrapper data={data}/>
                        </Box>
                    )}
                </Box>
            )}

            {isCompact && canUpdateCharger && (
                // No reserved height here: on this page the device card is followed by charging, the
                // schedule, the consumption chart and the sessions, so 68px held open for a fixed bar
                // would sit as a band of white between the device and what comes after it. The page
                // carries the clearance at its own foot instead.
                <RecordActionBar reserveSpace={false}>
                    {/* Both behind chargers.update, opening the same two modals. What you do to the
                        hardware stays in the device card below - a reset is not a page action. */}
                    <Box sx={squareActionSx}>
                        <Button
                            variant="contained"
                            sx={actionButtonSx('32px', 'secondary')}
                            startIcon={<PhotoCameraOutlined/>}
                            onClick={handleOpenImageModal}
                            aria-label={intl.formatMessage({id: 'photo'})}
                        />
                    </Box>
                    <Box sx={actionSlotSx(true)}>
                        <Button
                            variant="contained"
                            sx={actionButtonSx('32px', 'secondary')}
                            startIcon={<Edit/>}
                            onClick={handleOpenModal}
                        >
                            <FormattedMessage id="edit"/>
                        </Button>
                    </Box>
                </RecordActionBar>
            )}

            <DropZoneModal
                submitIsBlocked={blockSubmit}
                handleClose={handleCloseImageModal}
                open={openDropZone}
                title={uploadDialogTitle}
                handleFile={handleSetImage}
                handleSubmit={handleSubmit}
                isImage
            />
            <AMPModal
                handleClose={handleShowDeleteUserModal(false)}
                open={showDeleteModal}
                title={intl.formatMessage({id: 'unlinkUser'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <Box sx={modalBodySx}>
                    <Typography>
                        {intl.formatMessage(
                            {id: 'unlinkNameUserConfirm'},
                            {
                                name: data?.user
                                    ? `${data.user.firstName} ${data.user.lastName}`
                                    : data?.company?.name || 'Unknown'
                            }
                        )}
                    </Typography>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteUser(
                            deleteUser,
                            data.user?.id || (data.company as any)?.user?.id,
                            data.business?.id,
                            data?.id,
                            handleShowDeleteUserModal(false)
                        )}
                    >
                        {intl.formatMessage({id: 'unlink'})}
                    </Button>
                </Box>
            </AMPModal>
            <AMPModal
                handleClose={handleShowEditCostModal(false)}
                open={showEditCost}
                title={intl.formatMessage({id: 'currentCostCharger'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <EditChargerCostForm
                    onClose={handleShowEditCostModal(false)}
                    init={costInitValue}
                    chargerId={data.id}
                    businessId={businessId}
                    itemId={cost?.id}
                    closeModal={handleShowEditCostModal(false)}
                />
            </AMPModal>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <ChargerUpdateFormWrapper
                    handleClose={handleCloseModal}
                    authInfo={authInfo}
                    initialValue={data}
                />
            </AMPModal>
        </>
    );
};
